import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { fetchAdminStats } from "../../lib/adminApi";
import { useAdminAuth } from "../../context/AdminAuthContext";

function StatCard({ label, value, to, testId }) {
  const body = (
    <div className="rounded-lg border border-border bg-card p-5 transition-colors hover:border-gold/40" data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-muted">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-gold">{value ?? "—"}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function AdminDashboardPage() {
  const { admin } = useAdminAuth();
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(() => {
    fetchAdminStats()
      .then(setStats)
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load stats."));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div className="flex flex-col gap-8" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Control Room</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">Namaste, {admin?.name || "Admin"}.</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Manage the allow-list of enrolled users, view any member&apos;s saved profile, and add other admins.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Enrolments" value={stats?.enrolments_total} to="/admin/enrolments" testId="stat-enrolments" />
        <StatCard label="Enrolments active" value={stats?.enrolments_active} testId="stat-enrolments-active" />
        <StatCard label="Members" value={stats?.members_total} to="/admin/members" testId="stat-members" />
        <StatCard label="New requests" value={stats?.requests_new} to="/admin/requests" testId="stat-requests-new" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Link
          to="/admin/requests"
          className="rounded-lg border border-border bg-card p-6 hover:border-gold/40"
          data-testid="link-requests"
        >
          <div className="text-xs uppercase tracking-widest text-gold">Leads</div>
          <h3 className="mt-1 text-lg font-semibold">Access requests</h3>
          <p className="mt-1 text-sm text-muted">
            {stats?.requests_new ? (
              <span>
                <span className="text-gold">{stats.requests_new} new</span> request{stats.requests_new === 1 ? "" : "s"} waiting from the landing page.
              </span>
            ) : (
              "Landing-page leads land here. One click turns a lead into an active enrolment."
            )}
          </p>
        </Link>
      </div>

      <p className="text-xs text-muted">
        Manage the allow-list, insurer KPIs, reminder templates, budget categories and site
        pages from the sidebar on the left.
      </p>
    </div>
  );
}
